import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Upload, Loader2, FileText, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/PageHeader";
import { AnalysisResult } from "@/components/AnalysisResult";
import { ProfileSelect, useProfiles } from "@/components/ProfileSelect";
import { getPending, clearPending } from "@/lib/pendingDoc";
import { api, errMsg, fmtDate } from "@/lib/api";
import { toast } from "sonner";

const History = ({ items, onPick }) => (
  <div className="rounded-3xl border bg-white p-6">
    <h3 className="font-medium">Analisi precedenti</h3>
    {items.length === 0 && <p className="mt-2 text-sm text-slate-500">Ancora nessuna.</p>}
    {items.slice(0, 8).map((a) => (
      <button key={a.analysis_id} onClick={() => onPick(a)} className="mt-2 w-full text-left rounded-xl p-3 hover:bg-slate-50 transition-colors" data-testid={`history-${a.analysis_id}`}>
        <p className="text-sm font-medium truncate">{a.result?.titolo || a.filename}</p>
        <p className="text-xs text-slate-500">{fmtDate(a.created_at)}</p>
      </button>
    ))}
  </div>
);

export default function Analyze() {
  const profiles = useProfiles();
  const [profile, setProfile] = useState("me");
  const [save, setSave] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [drag, setDrag] = useState(false);
  const camRef = useRef();
  const fileRef = useRef();
  const pendingDone = useRef(false);

  useEffect(() => {
    api.get("/ai/analyses").then(({ data }) => setHistory(data));
  }, []);

  useEffect(() => {
    if (pendingDone.current) return;
    pendingDone.current = true;
    getPending().then((f) => {
      if (!f) return;
      clearPending();
      toast.success("Stiamo analizzando la lettera che hai caricato");
      run(f);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("save", save ? "true" : "false");
    fd.append("profile_id", profile === "me" ? "" : profile);
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/analyze", fd);
      setResult(data);
      setHistory((h) => [data, ...h]);
    } catch (e) {
      const msg = errMsg(e);
      toast.error(msg, e?.response?.status === 402 ? { action: { label: "Passa a Pro", onClick: () => (window.location.href = "/app/pro") } } : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="analyze-page">
      <PageHeader eyebrow="Come al CAF" title="Scatta e analizza" desc="Fotografa o carica una lettera, una bolletta, una cartella. Ti spieghiamo cosa fare." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {!result && (
            <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); run(e.dataTransfer.files[0]); }}
              className={`rounded-3xl border-2 border-dashed p-10 md:p-14 text-center transition-colors ${drag ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-300 bg-white"}`} data-testid="analyze-dropzone">
              {busy ? (
                <div className="py-6" data-testid="analyze-loading">
                  <Loader2 className="h-10 w-10 mx-auto animate-spin text-[#1E3A8A]" />
                  <p className="mt-4 font-medium">Sto leggendo il documento…</p>
                  <p className="text-sm text-slate-500">Di solito bastano 10-30 secondi.</p>
                </div>
              ) : (
                <>
                  <FileText className="h-10 w-10 mx-auto text-slate-400" />
                  <p className="mt-4 font-display text-xl font-medium">Trascina qui il documento</p>
                  <p className="text-sm text-slate-500">JPG, PNG, WEBP o PDF fino a 10 MB</p>
                  <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <Button className="rounded-full h-12 px-6" onClick={() => camRef.current.click()} data-testid="analyze-camera-btn"><Camera className="h-4 w-4 mr-2" /> Scatta foto</Button>
                    <Button variant="outline" className="rounded-full h-12 px-6" onClick={() => fileRef.current.click()} data-testid="analyze-upload-btn"><Upload className="h-4 w-4 mr-2" /> Carica file</Button>
                  </div>
                  <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => run(e.target.files[0])} data-testid="analyze-camera-input" />
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" hidden onChange={(e) => run(e.target.files[0])} data-testid="analyze-file-input" />
                </>
              )}
            </div>
          )}
          {result && (
            <>
              <Button variant="outline" className="rounded-full" onClick={() => setResult(null)} data-testid="analyze-new-btn"><RotateCcw className="h-4 w-4 mr-2" /> Analizza un altro documento</Button>
              <AnalysisResult analysis={result} />
            </>
          )}
        </div>
        <div className="space-y-6">
          <div className="rounded-3xl border bg-white p-6 space-y-5">
            <div>
              <p className="text-sm font-medium mb-2">Di chi è il documento?</p>
              <ProfileSelect value={profile} onChange={setProfile} profiles={profiles} testId="analyze-profile-select" />
              <Link to="/app/famiglia" className="mt-2 inline-block text-xs text-[#1E3A8A]">Aggiungi un familiare</Link>
            </div>
            <label className="flex items-center justify-between gap-3 text-sm">
              Salva nell'Archivio
              <Switch checked={save} onCheckedChange={setSave} data-testid="analyze-save-switch" />
            </label>
          </div>
          <History items={history} onPick={setResult} />
        </div>
      </div>
    </div>
  );
}
